<template>
  <div class="w-full">
    <!-- Loading State -->
    <div v-if="loading" class="relative py-1">
      <div class="absolute left-2 top-3 bottom-0 w-px bg-gray-200"></div>
      <div v-for="n in 3" :key="'sk-' + n" class="relative pl-6 pb-3 mt-3">
        <div class="absolute left-2 -translate-x-1/2 mt-1">
          <div class="w-5 h-5 rounded-full bg-gray-200 ring-2 ring-white animate-pulse"></div>
        </div>
        <div class="space-y-1">
          <div class="h-3 bg-gray-200 rounded animate-pulse" :style="{ width: [120, 100, 140][n - 1] + 'px' }"></div>
          <div class="h-2.5 bg-gray-100 rounded animate-pulse" :style="{ width: [80, 60, 90][n - 1] + 'px' }"></div>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else-if="!workItems || workItems.length === 0" class="text-gray-500 text-xs text-center py-3">
      No work activities recorded
    </div>

    <!-- Timeline Display -->
    <div v-else class="relative">
      <!-- Timeline line -->
      <div class="absolute left-2 top-3 bottom-0 w-px bg-gray-200"></div>

      <!-- Work items -->
      <div v-for="(item, index) in workItems" :key="item.id" class="relative group">
        <!-- Timeline node: the work type's own icon in a colored chip.
             Icon resolved from WorkType.icon (normalized) with a category
             fallback; color keeps the existing work-type color mapping. -->
        <div :class="[
          'absolute w-5 h-5 flex items-center justify-center mt-1 -translate-x-1/2',
          item.parent ? 'left-8' : 'left-2'
        ]">
          <div :class="[
            'w-5 h-5 rounded-full ring-2 ring-white flex items-center justify-center transition-all',
            getWorkTypeColor(item.work_type_details?.name)
          ]">
            <FontAwesomeIcon
              :icon="getWorkTypeIcon(item)"
              class="text-white"
              style="font-size: 9px;"
            />
          </div>
        </div>

        <!-- Content container -->
        <div :class="[
          'pb-3 mt-3',
          item.parent ? 'ml-12' : 'ml-6'
        ]">
          <!-- Content area -->
          <div :class="['rounded-lg p-2 -m-2', item.deleted_at ? 'opacity-50' : '']">
            <!-- Header row -->
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <!-- Parent indicator for child items -->
                  <FontAwesomeIcon
                    v-if="item.parent"
                    :icon="['fas', 'turn-down']"
                    class="text-xs text-gray-400 rotate-90"
                    title="Child work item"
                  />

                  <!-- Clinical Engagement Badge -->
                  <span
                    v-if="isClinicalEngagement(item)"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700"
                  >
                    Patient Outreach
                  </span>

                  <span :class="['text-xs font-medium truncate', item.deleted_at ? 'text-gray-400 line-through' : 'text-gray-900']">
                    {{ getWorkLabel(item) }}
                  </span>
                  <span v-if="item.deleted_at" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600">
                    Deleted{{ item.deleted_by_name ? ` by ${item.deleted_by_name}` : '' }}
                  </span>

                  <!-- Call outcome badge -->
                  <span
                    v-if="item.metadata?.call_outcome"
                    :class="[
                      'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                      {
                        'bg-green-100 text-green-700': item.metadata.call_outcome === 'reached' || item.metadata.call_outcome === 'completed',
                        'bg-yellow-100 text-yellow-700': item.metadata.call_outcome === 'voicemail',
                        'bg-red-100 text-red-700': item.metadata.call_outcome === 'no_answer' || item.metadata.call_outcome === 'refused' || item.metadata.call_outcome === 'wrong_number',
                      }
                    ]"
                  >{{ item.metadata.call_outcome.replace('_', ' ') }}</span>

                  <!-- Async engagement status badge -->
                  <span
                    v-if="item.async_engagement_status"
                    :class="[
                      'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                      {
                        'bg-yellow-100 text-yellow-700': item.async_engagement_status.status === 'pending',
                        'bg-blue-100 text-blue-700': item.async_engagement_status.status === 'viewed',
                        'bg-green-100 text-green-700': item.async_engagement_status.status === 'completed',
                        'bg-gray-100 text-gray-500': item.async_engagement_status.status === 'expired',
                      }
                    ]"
                  >
                    <FontAwesomeIcon :icon="['fas', 'paper-plane']" class="mr-1" />
                    Sent to {{ item.async_engagement_status.recipient_name }}
                    <span class="mx-1">·</span>
                    {{ item.async_engagement_status.status }}
                  </span>

                  <!-- Custom widget indicator -->
                  <FontAwesomeIcon
                    v-if="hasCustomDisplay(item)"
                    :icon="['fas', 'wand-magic-sparkles']"
                    class="text-xs text-primary-500"
                    title="Custom display"
                  />

                  <!-- Video indicator if session had video -->
                  <span
                    v-if="item.metadata?.session_type?.includes('with_video')"
                    class="text-xs text-purple-600"
                  >
                    <FontAwesomeIcon :icon="['fas', 'video']" class="mr-1" />
                    Video
                  </span>

                  <!-- View indicator on hover -->
                  <button
                    v-if="!isClinicalEngagement(item)"
                    @click.stop="openWorkDetail(item)"
                    class="text-xs text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary-700"
                    title="View details"
                  >
                    <FontAwesomeIcon :icon="['fas', 'eye']" />
                  </button>
                </div>
                <div class="flex items-center gap-2 mt-0.5">
                  <span class="text-xs text-gray-500">{{ formatTime(item.created_at) }}</span>
                  <span class="text-xs text-gray-400">•</span>
                  <span class="text-xs text-gray-600">{{ item.appuser_name }}</span>
                  <!-- Task type name display -->
                  <template v-if="item.tasktype_name">
                    <span class="text-xs text-gray-400">•</span>
                    <span class="text-xs text-blue-600 font-medium">{{ item.taskTypeName }}</span>
                  </template>
                </div>
              </div>

              <!-- Delete / Restore button -->
              <button
                v-if="item.deleted_at"
                @click.stop="restoreItem(item)"
                class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded hover:bg-green-50"
                title="Restore work item"
              >
                <FontAwesomeIcon :icon="['fas','rotate-left']" class="text-green-600 text-xs" />
              </button>
              <button
                v-else-if="canEditItem(item)"
                @click.stop="confirmDelete(item)"
                class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded hover:bg-red-50"
                title="Delete work item"
              >
                <FontAwesomeIcon :icon="['fas','trash']" class="text-red-500 text-xs" />
              </button>
            </div>

            <!-- Clinical Session Info -->
            <div v-if="isClinicalEngagement(item) && item.metadata" class="mt-1.5">
              <div class="bg-purple-50 rounded px-2 py-1 text-xs">
                <div v-if="item.metadata.session_completed_at" class="flex items-center gap-1 text-purple-700">
                  <FontAwesomeIcon :icon="['fas', 'check-circle']" />
                  <span>Session completed</span>
                </div>
                <div v-else-if="item.metadata.video_session?.status === 'scheduled'" class="flex flex-col gap-1">
                  <div class="flex items-center gap-1 text-purple-600">
                    <FontAwesomeIcon :icon="['fas', 'calendar-check']" />
                    <span>Scheduled{{ item.metadata.video_session.scheduled_at ? ' for ' + formatScheduledAt(item.metadata.video_session.scheduled_at) : '' }}</span>
                  </div>
                  <div v-if="item.metadata.video_session.join_url" class="flex items-center gap-1.5 mt-0.5">
                    <input
                      :value="item.metadata.video_session.join_url"
                      readonly
                      class="flex-1 px-1.5 py-0.5 bg-white border border-purple-200 rounded text-xs text-gray-600 truncate"
                      @click.stop
                    />
                    <button
                      @click.stop="copyLink(item.metadata.video_session.join_url)"
                      class="px-1.5 py-0.5 text-xs text-purple-600 hover:text-purple-800 border border-purple-200 rounded hover:bg-white transition-colors"
                    >Copy</button>
                    <button
                      @click.stop="emailInvite(item.id)"
                      :disabled="emailSending[item.id]"
                      class="px-1.5 py-0.5 text-xs text-purple-600 hover:text-purple-800 border border-purple-200 rounded hover:bg-white transition-colors disabled:opacity-50"
                    >{{ emailSentTo[item.id] ? 'Sent' : (emailSending[item.id] ? '...' : 'Email') }}</button>
                  </div>
                </div>
                <div v-else-if="item.metadata.video_session?.status === 'ended'" class="flex items-center gap-1 text-purple-600">
                  <FontAwesomeIcon :icon="['fas', 'check']" />
                  <span>Session ended</span>
                </div>
                <div v-else class="flex items-center gap-1 text-purple-600">
                  <FontAwesomeIcon :icon="['fas', 'clock']" />
                  <span>Session in progress</span>
                </div>

                <!-- Video session summary -->
                <div v-if="item.metadata.video_session" class="mt-1 flex items-center gap-2 text-gray-600">
                  <span v-if="item.metadata.video_session.duration_seconds">
                    {{ formatDuration(item.metadata.video_session.duration_seconds) }} call
                  </span>
                  <span v-if="getTranscriptSegmentCount(item) > 0">
                    · {{ getTranscriptSegmentCount(item) }} transcript segments
                  </span>
                </div>

                <!-- Full session notes on hover (covering-nurse quick-look).
                     The preview is hard-truncated; hovering shows the FULL notes.
                     The popover is TELEPORTED to <body> with fixed positioning so
                     it escapes the drawer's overflow-y-auto/overflow-hidden
                     ancestors (an absolute popover was being clipped by them).
                     Full text already lives on item.metadata.session_notes — no
                     fetch. Only armed when the notes exceed the inline preview. -->
                <div
                  v-if="item.metadata.session_notes"
                  class="mt-1 text-gray-700"
                  :class="{ 'cursor-help': isNotesTruncated(item.metadata.session_notes, 100) }"
                  @mouseenter="isNotesTruncated(item.metadata.session_notes, 100) && showNotesPopover(item.metadata.session_notes, $event)"
                  @mouseleave="scheduleHideNotesPopover"
                >
                  <span class="text-gray-500">Notes:</span>
                  {{ truncateText(item.metadata.session_notes, 100) }}
                </div>

                <button
                  @click.stop="openWorkDetail(item)"
                  class="mt-1 text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 hover:underline"
                >
                  <FontAwesomeIcon :icon="['fas', 'arrow-up-right-from-square']" />
                  <span>Open full session</span>
                </button>
              </div>
            </div>

            <!-- Work Item Display - Custom Widget or Generic or Healthcare Display -->
            <div v-else-if="item.metadata && hasRelevantMetadata(item.metadata)" class="mt-1.5">
              <!-- Use custom display widget if available (for code suggestions) -->
              <component
                v-if="getCustomDisplayWidget(item)"
                :is="getCustomDisplayWidget(item)"
                :work-item="item"
                :task-id="taskId"
                :task-type-id="taskTypeId"
                :task-type-name="taskTypeName"
                :task-metadata="taskMetadata"
                :compact="true"
                :parent-work-item="getParentWorkItem(item)"
                :children="workItemChildren[item.id] || []"
                :current-user="currentUser"
                @updated="fetchWorkItems"
              />

              <!-- Healthcare/Clinical metadata display (for call entries, outcomes, etc.) -->
              <div v-else-if="hasHealthcareMetadata(item.metadata)" class="bg-gray-50 rounded px-2 py-1 text-xs">
                <!-- Show call entries if present -->
                <div v-if="item.metadata.call_entries && item.metadata.call_entries.length > 0">
                  <div v-for="(entry, entryIndex) in item.metadata.call_entries"
                       :key="`call-${item.id}-${entryIndex}`"
                       :class="{'mt-2 pt-2 border-t border-gray-200': entryIndex > 0}">

                    <!-- Call Outcome -->
                    <div v-if="entry.call_outcome" class="flex items-start gap-1">
                      <span class="text-gray-600 shrink-0">Outcome:</span>
                      <span class="font-medium" :class="getCallOutcomeClass(entry.call_outcome)">
                        {{ entry.call_outcome }}
                      </span>
                    </div>

                    <!-- Medication Category -->
                    <div v-if="entry.medication_category" class="flex items-center gap-1 mt-0.5">
                      <FontAwesomeIcon :icon="['fas','pills']" class="text-xs text-gray-400" />
                      <span class="text-gray-600">{{ entry.medication_category }}</span>
                    </div>

                    <!-- Call Notes -->
                    <div v-if="entry.call_note" class="mt-0.5 text-gray-600 max-h-16 overflow-y-auto">
                      <span class="text-gray-500">Note:</span> {{ entry.call_note }}
                    </div>
                  </div>
                </div>

                <!-- Show generic outcome if present and no call entries -->
                <div v-else-if="item.metadata.outcome" class="flex items-center gap-1">
                  <span class="text-gray-600">Outcome:</span>
                  <span class="font-medium" :class="getOutcomeClass(item.metadata.outcome)">
                    {{ formatOutcome(item.metadata.outcome) }}
                  </span>
                </div>

                <!-- Show notes if present -->
                <div v-if="item.metadata.notes" class="mt-0.5 text-gray-600">
                  {{ item.metadata.notes }}
                </div>

                <!-- Show additional notes if present -->
                <div v-if="item.metadata.additional_notes" class="mt-0.5 text-gray-600">
                  <span class="text-gray-500">Additional:</span> {{ item.metadata.additional_notes }}
                </div>

                <!-- Show duration if present -->
                <div v-if="item.metadata.duration" class="flex items-center gap-1 mt-0.5">
                  <FontAwesomeIcon :icon="['fas','clock']" class="text-xs text-gray-400" />
                  <span class="text-gray-600">{{ item.metadata.duration }} min</span>
                </div>

                <!-- Show method if present -->
                <div v-if="item.metadata.method" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 mt-0.5">
                  {{ item.metadata.method }}
                </div>

                <!-- Indicator that there's more to see -->
                <button
                  v-if="hasMoreFields(item)"
                  @click.stop="openWorkDetail(item)"
                  class="mt-1 text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1 hover:underline"
                >
                  <FontAwesomeIcon :icon="['fas', 'circle-info']" class="text-xs" />
                  <span>View all details</span>
                </button>
              </div>

              <!-- Otherwise use generic display for other work types -->
              <GenericWorkDisplay
                v-else
                :work-item="item"
                :metadata="item.metadata"
                :compact="true"
                :current-user="currentUser"
                @updated="fetchWorkItems"
              />
            </div>

            <!-- Attachments Section — renders for EVERY work item (survey,
                 generic, healthcare, or attachment-only), independent of the
                 display-type branch above. Uploader is 50MB / all doc types
                 (WorkFileUploader → /api/media/), gated only by canEditItem. -->
            <div v-if="(item.metadata && item.metadata.uploaded_images && item.metadata.uploaded_images.length > 0) || canEditItem(item)"
                 class="mt-2 pt-2 border-t border-gray-200">
              <div class="text-xs text-gray-500 font-medium mb-1">Attachments:</div>

              <!-- File Uploader - only show if user can edit -->
              <div v-if="canEditItem(item)" class="mb-1">
                <WorkFileUploader
                  :related-object-id="item.id"
                  :related-object-content-type-model="'work'"
                  @file-uploaded="(fileData) => addImage(item, fileData)"
                />
              </div>

              <!-- Display uploaded images/files -->
              <div v-if="item.metadata && item.metadata.uploaded_images && item.metadata.uploaded_images.length > 0">
                <div v-for="image in item.metadata.uploaded_images"
                     :key="image.key"
                     class="flex items-center gap-1 mb-1">
                  <MediaViewer
                    :can-delete="canEditItem(item) && !uploadingImage[item.id]"
                    :media-key="image.key"
                    @media-deleted="(key) => removeImage(item, key)"
                  />
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Separator line (except for last item) -->
        <div v-if="index < workItems.length - 1" :class="[
          'border-b border-gray-100 mb-1',
          item.parent ? 'ml-10' : 'ml-6'
        ]"></div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
    <div v-if="showDeleteModal" class="fixed inset-0" style="z-index: 99999;">
      <div class="fixed inset-0 bg-black/50" @click="cancelDelete"></div>
      <div class="fixed inset-0 flex items-center justify-center" style="pointer-events: none;">
        <div class="bg-white rounded-lg text-left overflow-hidden shadow-xl max-w-lg w-full mx-4" style="pointer-events: auto;">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <FontAwesomeIcon :icon="['fas','exclamation-triangle']" class="text-red-600" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Delete Work Item
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Are you sure you want to delete this work item? It can be restored later.
                  </p>
                  <div v-if="itemToDelete" class="mt-3 bg-gray-50 rounded p-3">
                    <p class="text-xs font-medium text-gray-700">
                      {{ itemToDelete.work_type_details?.display_name || 'Work Activity' }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">
                      {{ formatTime(itemToDelete.created_at) }} by {{ itemToDelete.appuser_name }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="deleteItem"
              :disabled="deleting"
              class="btn-danger w-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {{ deleting ? 'Deleting...' : 'Delete' }}
            </button>
            <button
              @click="cancelDelete"
              :disabled="deleting"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Work Detail Modal -->
    <WorkDetailModal
      :work-item="selectedWorkItem"
      :is-open="showDetailModal"
      @close="closeWorkDetail"
    />

    <!-- Session-notes hover popover — teleported to body + fixed position so it
         is never clipped by the drawer's scroll/overflow ancestors. -->
    <Teleport to="body">
      <div
        v-if="notesPopover.text"
        class="fixed z-[100000] w-96 max-w-[26rem]"
        :style="{ top: notesPopover.y + 'px', left: notesPopover.x + 'px' }"
        role="tooltip"
        @mouseenter="cancelHideNotesPopover"
        @mouseleave="scheduleHideNotesPopover"
      >
        <div class="max-h-72 overflow-y-auto whitespace-pre-line rounded-md bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-xl">
          <span class="mb-1 block font-semibold text-gray-300">Session notes</span>{{ notesPopover.text }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import WorkDetailModal from "@/components/Portal/Task_Tables/Work_Widgets/WorkDetailModal.vue";
import GenericWorkDisplay from './GenericWorkDisplay.vue';
import CodeSuggestionDisplay from './CodeSuggestionDisplay.vue';
import QACodeSuggestionDisplay from './QACodeSuggestionDisplay.vue';
import WorkFileUploader from '@/components/widgetcomponents/WorkFileUploader.vue';
import MediaViewer from '@/components/widgetcomponents/MediaViewer.vue';

export default {
  components: {
    FontAwesomeIcon,
    WorkDetailModal,
    GenericWorkDisplay,
    CodeSuggestionDisplay,
    QACodeSuggestionDisplay,
    WorkFileUploader,
    MediaViewer
  },
  props: {
    opportunities: {
      type: Array,
      required: false
    },
    taskTypeId: {
      type: Number,
      required: false,
      default: null
    },
    taskTypeName: {
      type: String,
      required: false,
      default: ''
    },
    taskId: {
      type: Number,
      required: false,
      default: null
    },
    patientIdentifier: {
      type: [String, Number],
      required: false,
      default: null
    },
    metricIds: {
      type: Array,
      required: false,
      default: null
    },
    taskMetadata: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      workItems: [],
      loading: true,
      showDeleteModal: false,
      itemToDelete: null,
      deleting: false,
      showDetailModal: false,
      selectedWorkItem: null,
      uploadingImage: {},
      emailSending: {},
      emailSentTo: {},
      // Session-notes hover popover (teleported, fixed-position).
      notesPopover: { text: '', x: 0, y: 0 },
    };
  },
  computed: {
    currentUser() {
      return this.$store?.getters['auth/current_user'] || null;
    },
    workItemChildren() {
      const dict = {};
      this.workItems.forEach(item => {
        if (item.parent) {
          if (!dict[item.parent]) {
            dict[item.parent] = [];
          }
          dict[item.parent].push(item);
        }
      });

      // Sort each array by created_at
      Object.keys(dict).forEach(key => {
        dict[key].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });

      return dict;
    }
  },
  beforeUnmount() {
    this.cancelHideNotesPopover();
  },
  methods: {
    // Check if work item is a clinical engagement
    isClinicalEngagement(item) {
      if (item.work_type === 19 || item.work_type_details?.id === 19) return true;
      const sessionType = item.metadata?.session_type;
      return sessionType && sessionType.includes('clinical_session');
    },

    getTranscriptSegmentCount(item) {
      return item.metadata?.transcript?.segments?.length || 0;
    },

    async emailInvite(workId) {
      if (this.emailSending[workId] || this.emailSentTo[workId]) return;
      this.emailSending = { ...this.emailSending, [workId]: true };
      try {
        const { data } = await axios.post(`/api/work/${workId}/video/send-invite/`);
        if (data.sent) {
          this.emailSentTo = { ...this.emailSentTo, [workId]: data.email };
          this.$root.$emit('show-success-toast', `Invite sent to ${data.email}`);
        } else {
          this.$root.$emit('show-error-toast', data.reason || 'No email on file');
        }
      } catch {
        this.$root.$emit('show-error-toast', 'Failed to send invite');
      } finally {
        this.emailSending = { ...this.emailSending, [workId]: false };
      }
    },

    copyLink(url) {
      navigator.clipboard.writeText(url)
        .then(() => this.$root.$emit('show-success-toast', 'Link copied'))
        .catch(() => this.$root.$emit('show-error-toast', 'Failed to copy'));
    },

    formatScheduledAt(isoStr) {
      if (!isoStr) return '';
      try {
        return new Date(isoStr).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
      } catch { return isoStr; }
    },

    formatDuration(seconds) {
      if (!seconds) return '';
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      if (m === 0) return `${s}s`;
      return `${m}m ${s}s`;
    },

    hasCustomDisplay(item) {
      return this.getCustomDisplayWidget(item) !== null;
    },

    getCustomDisplayWidget(item) {
      const name = (item.work_type_name || item.work_type_details?.name || '').toLowerCase();
      if (name.includes('encounter_review_add_code') || name.includes('code suggestion')) {
        return 'CodeSuggestionDisplay';
      }
      if (name.includes('code_qa') || name.includes('qa review') || name.includes('coder_response') || name.includes('coder response')) {
        return 'QACodeSuggestionDisplay';
      }
      return null;
    },

    getWorkLabel(item) {
      const wt = (item.work_type_name || item.work_type_details?.name || '').toLowerCase();
      const defaultLabel = item.work_type_details?.display_name || item.work_type_details?.name || 'Work Activity';

      const isQA = wt.includes('code_qa') || wt.includes('qa review');
      const isCoderResponse = wt.includes('coder_response') || wt.includes('coder response');

      if (!isQA && !isCoderResponse) return defaultLabel;

      // Extract tier from the task the work was logged against: e.g.
      // "Suspect 2LR Review" → "2LR", "Confirm for POC Partner" → "Confirm".
      const taskName = item.tasktype_name || '';
      const match = taskName.match(/\b(\d+LR|Confirm)\b/i);
      const tier = match ? match[1].toUpperCase() : null;

      if (!tier) return defaultLabel;
      return isQA ? `${tier} QA` : `${tier} Response`;
    },

    getParentWorkItem(item) {
      if (!item.parent) return null;
      return this.workItems.find(w => w.id === item.parent);
    },

    sortWorkItemsWithChildren(items) {
      const sorted = [...items].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const childrenMap = new Map();
      const parentItems = [];

      sorted.forEach(item => {
        if (item.parent) {
          if (!childrenMap.has(item.parent)) {
            childrenMap.set(item.parent, []);
          }
          childrenMap.get(item.parent).push(item);
        } else {
          parentItems.push(item);
        }
      });

      const result = [];

      parentItems.forEach(parent => {
        result.push(parent);
        const children = childrenMap.get(parent.id);
        if (children) {
          // Sort children earliest to latest
          children.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          result.push(...children);
        }
      });

      return result;
    },

    async fetchWorkItems() {
      // Work_Timeline has three prop modes with precedence:
      //   1. opportunities → show all work for the opportunity/ies
      //      (default for task side panels — work lives on the 1LR task
      //      but must surface on 3LR/Confirm review tiers)
      //   2. patientIdentifier → show all work for the patient/entity
      //   3. taskId → show work only for this specific task
      //      (rare; used when no opportunity or patient context exists)
      //
      // taskId is also a forwarding prop to child write widgets
      // (CodeSuggestionDisplay, QACodeSuggestionDisplay) and must not
      // gate the read when opportunities are present.
      if (this.opportunities && this.opportunities.length > 0) {
        try {
          const opportunityIds = this.opportunities.map(opp => opp.id);
          const response = await axios.get('/api/work/', {
            params: {
              opportunity__in: opportunityIds.join(','),
              include_deleted: 'true'
            }
          });
          this.workItems = this.sortWorkItemsWithChildren(response.data);
        } catch (error) {
          console.error('Error fetching work items:', error);
          this.workItems = [];
        } finally {
          this.loading = false;
        }
        return;
      }

      if (this.patientIdentifier) {
        try {
          const params = { patient_identifier: this.patientIdentifier, include_deleted: 'true' };
          if (this.metricIds && this.metricIds.length > 0) {
            params.metric_ids = this.metricIds.join(',');
          }
          const response = await axios.get('/api/work/', { params });
          this.workItems = this.sortWorkItemsWithChildren(response.data);
        } catch (error) {
          console.error('Error fetching work items:', error);
          this.workItems = [];
        } finally {
          this.loading = false;
        }
        return;
      }

      if (this.taskId) {
        try {
          const response = await axios.get('/api/work/', {
            params: { task: this.taskId, include_deleted: 'true' }
          });
          this.workItems = this.sortWorkItemsWithChildren(response.data);
        } catch (error) {
          console.error('Error fetching work items:', error);
          this.workItems = [];
        } finally {
          this.loading = false;
        }
        return;
      }

      this.loading = false;
    },

    // Updated method to handle clinical engagements differently
    openWorkDetail(item) {
      if (this.isClinicalEngagement(item)) {
        // Open clinical engagement in new tab
        const route = this.$router.resolve({
          name: 'ClinicalEngagement',
          params: { workId: item.id }
        });
        window.open(route.href, '_blank');
      } else {
        // Open regular work detail modal
        this.selectedWorkItem = item;
        this.showDetailModal = true;
      }
    },

    closeWorkDetail() {
      this.showDetailModal = false;
      setTimeout(() => {
        this.selectedWorkItem = null;
      }, 300);
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    // True when session notes are longer than the inline preview — i.e. there
    // is more to reveal, so the hover popover is worth showing.
    isNotesTruncated(text, maxLength) {
      return !!text && text.length > maxLength;
    },

    // Show / move / hide the teleported full-notes popover. Positioned near the
    // cursor and clamped to the viewport so long notes never run off-screen.
    showNotesPopover(text, evt) {
      this.cancelHideNotesPopover();
      this.notesPopover.text = text;
      // Anchor to the preview element (not the moving cursor) so the popover is
      // stable and the user can move into it to read/scroll long notes.
      this.positionNotesPopover(evt.currentTarget || evt.target);
    },
    positionNotesPopover(anchorEl) {
      const pad = 12;
      const width = 384; // w-96
      const maxH = 288;  // max-h-72
      const r = anchorEl && anchorEl.getBoundingClientRect
        ? anchorEl.getBoundingClientRect()
        : { left: pad, bottom: pad, top: pad };
      let x = r.left;
      let y = r.bottom + 6;
      if (x + width + pad > window.innerWidth) x = window.innerWidth - width - pad;
      // Flip above the anchor if there isn't room below.
      if (y + maxH + pad > window.innerHeight) y = Math.max(pad, r.top - maxH - 6);
      this.notesPopover.x = Math.max(pad, x);
      this.notesPopover.y = Math.max(pad, y);
    },
    scheduleHideNotesPopover() {
      // Grace period so the cursor can travel from the preview into the popover.
      this.cancelHideNotesPopover();
      this._notesHideTimer = setTimeout(() => { this.notesPopover.text = ''; }, 160);
    },
    cancelHideNotesPopover() {
      if (this._notesHideTimer) { clearTimeout(this._notesHideTimer); this._notesHideTimer = null; }
    },

    hasMoreFields(item) {
      // Check if there are more fields in metadata than what we're showing in the timeline
      if (!item.metadata) return false;

      // Fields we already show in the timeline
      const shownFields = [
        'call_entries', 'outcome', 'notes', 'additional_notes',
        'duration', 'method', 'timestamp', 'task_metadata', 'visible_fields',
        'session_type', 'session_notes', 'session_completed_at', 'widget_type',
        'uploaded_images'
      ];

      // Check if there are other fields with values
      const otherFields = Object.keys(item.metadata).filter(key => {
        return !shownFields.includes(key) &&
               item.metadata[key] !== null &&
               item.metadata[key] !== undefined &&
               item.metadata[key] !== '';
      });

      return otherFields.length > 0;
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.showDeleteModal = true;
    },

    cancelDelete() {
      this.showDeleteModal = false;
      this.itemToDelete = null;
    },

    async deleteItem() {
      if (!this.itemToDelete) return;

      this.deleting = true;
      try {
        await axios.delete(`/api/work/${this.itemToDelete.id}/`);
        await this.fetchWorkItems();
        this.showDeleteModal = false;
        this.itemToDelete = null;
        this.$emit('work-deleted');
      } catch (error) {
        console.error('Error deleting work item:', error);
      } finally {
        this.deleting = false;
      }
    },

    async restoreItem(item) {
      try {
        await axios.post(`/api/work/${item.id}/restore/`);
        await this.fetchWorkItems();
      } catch (error) {
        console.error('Error restoring work item:', error);
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return 'Unknown';

      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    },

    // Resolve the FontAwesome icon for a work item from its work type.
    // The stored WorkType.icon is inconsistent (three historical formats:
    //   ['fas','x']  ["fas","x"]  and bare "x"), a couple are Heroicon names
    // that aren't valid FA, and most work types have no icon at all. This
    // normalizes all of that at the render layer and falls back to a sensible
    // category icon (keyed on the work type name) when there's nothing usable,
    // so the timeline always shows a correct, registered icon.
    getWorkTypeIcon(item) {
      const raw = item?.work_type_details?.icon;
      const name = this._normalizeIconName(raw);
      if (name) return ['fas', name];
      return ['fas', this._fallbackIconName(item)];
    },

    // Pull a bare FA icon name out of any of the stored formats, remapping the
    // known non-FA (Heroicon) names to their FA equivalents. Returns null when
    // the value is empty/unusable so the caller can fall back.
    _normalizeIconName(raw) {
      if (!raw || typeof raw !== 'string') return null;
      let name = raw.trim();
      // Array-string forms: ['fas','file-invoice'] or ["fas","file-invoice"].
      // Grab the LAST quoted token (the icon name; the first is the style prefix).
      const quoted = name.match(/['"]([^'"]+)['"]/g);
      if (quoted && quoted.length) {
        name = quoted[quoted.length - 1].replace(/['"]/g, '').trim();
      }
      if (!name || name === 'fas' || name === 'far' || name === 'fal') return null;
      // Remap Heroicon names that were stored but aren't valid FontAwesome.
      const remap = {
        'document-check': 'file-circle-check',
        'document-text': 'file-lines',
      };
      return remap[name] || name;
    },

    // Category fallback by work type name — mirrors getWorkTypeColor's keying.
    _fallbackIconName(item) {
      const type = (item?.work_type_name || item?.work_type_details?.name || '').toLowerCase();
      const map = [
        ['call', 'phone'],
        ['outreach', 'phone'],
        ['clinical', 'notes-medical'],
        ['assessment', 'clipboard-list'],
        ['survey', 'clipboard-list'],
        ['screener', 'clipboard-list'],
        ['medication', 'pills'],
        ['pharmacy', 'pills'],
        ['qa', 'magnifying-glass'],
        ['review', 'magnifying-glass'],
        ['code', 'file-invoice'],
        ['attestation', 'file-circle-check'],
        ['graduation', 'user-check'],
        ['follow', 'clipboard-check'],
        ['communication', 'file-lines'],
        ['coordination', 'file-lines'],
      ];
      for (const [key, icon] of map) {
        if (type.includes(key)) return icon;
      }
      return 'clipboard'; // generic work
    },

    getWorkTypeColor(workType) {
      const colorMap = {
        'call': 'bg-primary-500',
        'outreach': 'bg-purple-500',
        'assessment': 'bg-primary-500',
        'intervention': 'bg-green-500',
        'follow_up': 'bg-yellow-500',
        'documentation': 'bg-gray-500',
        'review': 'bg-primary-500',
        'code': 'bg-primary-500',
        'suggestion': 'bg-primary-500',
        'qa': 'bg-green-500',
        'clinical': 'bg-purple-600'
      };

      const type = workType?.toLowerCase() || '';
      for (const [key, color] of Object.entries(colorMap)) {
        if (type.includes(key)) return color;
      }
      return 'bg-gray-400';
    },

    hasRelevantMetadata(metadata) {
      if (!metadata) return false;

      // Don't show regular metadata display for clinical sessions (they have their own display)
      if (metadata.session_type?.includes('clinical_session')) {
        return false;
      }

      const systemFields = ['timestamp', 'task_metadata', 'widget_type'];
      const hasUserData = Object.keys(metadata).some(key =>
        !systemFields.includes(key) &&
        metadata[key] !== null &&
        metadata[key] !== undefined &&
        metadata[key] !== ''
      );

      return hasUserData;
    },

    // Check if metadata contains healthcare-specific fields
    hasHealthcareMetadata(metadata) {
      if (!metadata) return false;
      return metadata.call_entries || metadata.outcome || metadata.notes ||
             metadata.additional_notes || metadata.duration || metadata.method;
    },

    getOutcomeClass(outcome) {
      const lowerOutcome = outcome?.toLowerCase() || '';
      if (lowerOutcome.includes('success') || lowerOutcome.includes('complete')) {
        return 'text-green-700';
      }
      if (lowerOutcome.includes('fail') || lowerOutcome.includes('unable')) {
        return 'text-red-700';
      }
      if (lowerOutcome.includes('pending') || lowerOutcome.includes('in progress')) {
        return 'text-yellow-700';
      }
      return 'text-gray-700';
    },

    getCallOutcomeClass(outcome) {
      const lowerOutcome = outcome?.toLowerCase() || '';

      // Positive outcomes
      if (lowerOutcome.includes('picked up') ||
          lowerOutcome.includes('successful') ||
          lowerOutcome.includes('completed') ||
          lowerOutcome.includes('confirmed')) {
        return 'text-green-700';
      }

      // Negative outcomes
      if (lowerOutcome.includes('refused') ||
          lowerOutcome.includes('declined') ||
          lowerOutcome.includes('unable') ||
          lowerOutcome.includes('no answer')) {
        return 'text-red-700';
      }

      // Pending/In Progress outcomes
      if (lowerOutcome.includes('voicemail') ||
          lowerOutcome.includes('left message') ||
          lowerOutcome.includes('pending') ||
          lowerOutcome.includes('scheduled')) {
        return 'text-yellow-700';
      }

      // Neutral/Informational
      if (lowerOutcome.includes('spoke with') ||
          lowerOutcome.includes('discussed')) {
        return 'text-blue-700';
      }

      return 'text-gray-700';
    },

    formatOutcome(outcome) {
      // Capitalize first letter of each word
      return outcome.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },

    canEditItem(item) {
      if (item.appuser !== this.currentUser?.id) return false;
      return this.taskId == null || item.task === this.taskId;
    },

    async addImage(item, imageData) {
      this.uploadingImage[item.id] = true;

      try {
        const updatedImages = [...(item.metadata.uploaded_images || [])];

        const newImage = {
          key: imageData.key,
          url: imageData.url || null,
          filename: imageData.filename,
          uploaded_by: {
            appuser_id: this.currentUser?.id,
            appuser_name: this.currentUser ? `${this.currentUser.last_name}, ${this.currentUser.first_name}` : 'Unknown User',
            created_at: new Date().toISOString()
          }
        };

        updatedImages.push(newImage);

        await axios.patch(`/api/work/${item.id}/`, {
          metadata: {
            ...item.metadata,
            uploaded_images: updatedImages
          }
        });

        await this.fetchWorkItems();
      } catch (error) {
        console.error('Error adding image:', error);
        alert('Failed to add image. Please try again.');
      } finally {
        this.uploadingImage[item.id] = false;
      }
    },

    async removeImage(item, deletedKey) {
      this.uploadingImage[item.id] = true;

      try {
        const updatedImages = (item.metadata.uploaded_images || []).filter(
          img => img.key !== deletedKey
        );

        await axios.patch(`/api/work/${item.id}/`, {
          metadata: {
            ...item.metadata,
            uploaded_images: updatedImages
          }
        });

        await this.fetchWorkItems();
      } catch (error) {
        console.error('Error removing image:', error);
        alert('Failed to remove image. Please try again.');
      } finally {
        this.uploadingImage[item.id] = false;
      }
    },

  },

  watch: {
    opportunities: {
      handler() {
        this.fetchWorkItems();
      },
      immediate: true
    },
    patientIdentifier: {
      handler(val) {
        if (val) this.fetchWorkItems();
      },
      immediate: true
    },
    metricIds: {
      handler() {
        if (this.patientIdentifier) this.fetchWorkItems();
      }
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
